/**
 * Pure context-menu geometry kernels for the sidebar — extracted verbatim
 * (behavior-preserving) from the inline position math in the conversation /
 * module context-menu handlers in layout/Sidebar.tsx.
 *
 * The stateful open/close/toggle wiring lives in `useSidebarContextMenus`; these
 * pure helpers isolate the size constants and the two distinct positioning
 * formulas (right-click at cursor vs. actions-button anchored below the button
 * vs. mobile center-of-screen) so they can be characterization-tested without a
 * DOM and so both menus stay byte-identical to the original.
 */

import { clampMenuPosition } from './utils';

/** Conversation context-menu dimensions (right-click + actions-button + delete). */
export const CONVERSATION_MENU_WIDTH = 190;
export const CONVERSATION_MENU_HEIGHT = 196;
/** Module context-menu dimensions (rename + archive + delete; shorter than conversation). */
export const MODULE_MENU_WIDTH = 190;
export const MODULE_MENU_HEIGHT = 173;
/** Screen padding kept between a clamped menu and the viewport edge. */
export const MENU_SCREEN_PADDING = 8;

/**
 * Position for a right-click / long-press context menu opened AT the cursor.
 * Mirrors `handleConversationContextMenu` / `handleModuleContextMenu`.
 */
export function positionMenuAtCursor(
  clientX: number,
  clientY: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return clampMenuPosition({
    x: clientX,
    y: clientY,
    width,
    height,
    padding: MENU_SCREEN_PADDING,
  });
}

/**
 * Position for a menu anchored to an actions button: left-aligned to the
 * button's left edge, 6px below its bottom. Mirrors the
 * `handleConversationActionsClick` / `handleModuleActionsClick` formula.
 */
export function positionMenuBelowButton(
  rect: { left: number; bottom: number },
  width: number,
  height: number,
): { x: number; y: number } {
  return clampMenuPosition({
    x: rect.left,
    y: rect.bottom + 6,
    width,
    height,
    padding: MENU_SCREEN_PADDING,
  });
}

/**
 * Position for the mobile module long-press menu — centered on screen.
 * Mirrors `handleModuleLongPress`, which intentionally does NOT clamp (it uses
 * a center-of-viewport formula rather than `clampMenuPosition`).
 */
export function positionMenuCentered(
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: Math.round((window.innerWidth - width) / 2),
    y: Math.round((window.innerHeight - height) / 2),
  };
}
